import { forwardRef } from 'react';
import styled from '@emotion/styled';

import { createChainedFunction, noop } from '@toolbox/utils';
import { useRadioGroupContext } from './RadioGroupContext';
import { Text } from '../typography';
import { Box } from '../Box';
import { Icon } from '../Icon';
import { icons } from '../Icon/icons';

import type { RadioCardProps } from './types';

const IconWrapper = styled(Box)`
  & img {
    height: ${({ theme }) => theme.iconSizes.lg}px;
    width: ${({ theme }) => theme.iconSizes.lg}px;
  }

  & svg {
    height: ${({ theme }) => theme.iconSizes.xl}px;
    width: ${({ theme }) => theme.iconSizes.xl}px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    height: auto;
  }
`;

const StyledRadioCardV3 = styled.label`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 121px;
  color: ${({ theme }) => theme.palette.colors.primary};
  background-color: #f7f8fa;
  border: 1px solid ${({ theme }) => theme.palette.colors.gray[300]};
  border-radius: 2px;
  cursor: pointer;
  row-gap: ${({ theme }) => theme.spacing(3)};
  flex-direction: column;
  text-align: center;

  input {
    border: 0;
    clip: rect(0px, 0px, 0px, 0px);
    padding: 0;
    overflow: hidden;
    white-space: nowrap;
    position: absolute;
  }

  &:focus-within {
    box-shadow: 0 0 0 4px ${({ theme }) => theme.palette.colors.focus};
  }

  &:hover {
    border: 1px solid ${({ theme }) => theme.palette.colors.primary};
  }

  img {
    margin: 0;
  }

  svg {
    margin: 0;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding-left: ${({ theme }) => theme.spacing(4)};
    padding-right: ${({ theme }) => theme.spacing(4)};

    img {
      display: block;
    }

    svg {
      display: block;
    }
  }
`;

export const RadioCardV3 = forwardRef<HTMLInputElement, Omit<RadioCardProps, 'iconHover'>>(
  function RadioCard(props, ref) {
    const {
      label,
      checked: checkedProp,
      name: nameProp,
      onChange: onChangeProp,
      icon,
      ...radioProps
    } = props;

    const radioGroup = useRadioGroupContext();
    const doesIconExist = typeof icon === 'string' && icon in icons;

    const checked = checkedProp ?? (radioGroup && radioGroup.value === props.value);
    const name = nameProp ?? radioGroup?.name;

    const handleChange = createChainedFunction(onChangeProp, radioGroup?.onChange);

    return (
      <StyledRadioCardV3 onClick={() => handleChange(props.value)}>
        <input
          {...radioProps}
          tabIndex={0}
          type="radio"
          ref={ref}
          name={name}
          checked={checked}
          // suppress warnings as we handle changes on click
          onChange={noop}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === 'Space') {
              handleChange(props.value);
            }
          }}
          onClick={(e) => e.stopPropagation()}
        />

        <IconWrapper>{icon && (doesIconExist ? <Icon type={icon} /> : icon)}</IconWrapper>

        <Text css={{ fontSize: 16 }} className="label" variant="b2" as="span">
          {label}
        </Text>
      </StyledRadioCardV3>
    );
  },
);
